import React, { useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getSEOLanguage } from '../utils/seoHelpers';
import SEOMetaTags from '../components/SEOMetaTags';
import usePageTitle from '../hooks/usePageTitle';
import useGoogleAds from '../hooks/useGoogleAds';

// Importar las traducciones directamente para SEO
import esTranslations from '../locales/es.json';
import caTranslations from '../locales/ca.json';

const translations = {
    es: esTranslations,
    ca: caTranslations
};

const ReformationsLanding = () => {
    const { t, language } = useLanguage();
    const { location: urlLocation } = useParams();
    const routeLocation = useLocation();
    const { trackPageView, trackLead, trackCTAClick } = useGoogleAds();

    usePageTitle('reformsLanding');

    // Mapeo de ubicaciones URL a nombres legibles
    const locationMap = {
        'barbera-del-valles': 'Barberà del Vallès',
        'montcada-i-reixac': 'Montcada i Reixac',
        'ripollet': 'Ripollet',
        'castellar-del-valles': 'Castellar del Vallès',
        'sant-quirze-del-valles': 'Sant Quirze del Vallès',
        'palau-solita-i-plegamans': 'Palau-solità i Plegamans',
        'santa-perpetua-de-mogoda': 'Santa Perpètua de Mogoda',
        'badia-del-valles': 'Badia del Vallès',
        'sentmenat': 'Sentmenat',
        'polinya': 'Polinyà',
        'ullastrell': 'Ullastrell',
        'vacarisses': 'Vacarisses',
        'matadepera': 'Matadepera'
    };

    // Detectar tipo de servicio desde la ruta
    const serviceType = useMemo(() => {
        const path = routeLocation.pathname;
        if (path.includes('reforma-integral')) return 'integral';
        if (path.includes('reforma-bano')) return 'bano';
        if (path.includes('reforma-cocina')) return 'cocina';
        return 'general';
    }, [routeLocation.pathname]);

    // Obtener nombre legible de la ubicación
    const locationName = urlLocation ? locationMap[urlLocation] || urlLocation : null;

    // Determinar el idioma SEO según la lógica (SOLO para meta tags)
    const seoLanguage = getSEOLanguage(language);
    const seoTranslations = translations[seoLanguage];

    // Título visible para el USUARIO (usa su idioma preferido)
    const getCustomTitle = () => {
        if (!locationName) return t('reformsLanding.hero.title');

        const serviceTexts = {
            'integral': `Reforma integral en ${locationName}`,
            'bano': `Reforma de baño en ${locationName}`,
            'cocina': `Reforma de cocina en ${locationName}`,
            'general': `Reformas en ${locationName}`
        };

        return serviceTexts[serviceType] || t('reformsLanding.hero.title');
    };

    // Descripción visible para el USUARIO (usa su idioma preferido)
    const getCustomDescription = () => {
        if (!locationName) return t('reformsLanding.hero.description');

        const serviceDescriptions = {
            'integral': `Especialistas en reformas integrales en ${locationName}. Transformamos tu hogar completamente con garantía de calidad. Presupuesto gratuito sin compromiso.`,
            'bano': `Expertos en reformas de baño en ${locationName}. Diseño moderno, acabados de calidad y plazos cumplidos. Solicita tu presupuesto gratis.`,
            'cocina': `Reformas de cocina en ${locationName} con diseño personalizado. Funcionalidad y estilo para tu cocina ideal. Presupuesto sin compromiso.`,
            'general': `Empresa de reformas de confianza en ${locationName}. Más de 150 proyectos completados con garantía de 2 años. Presupuesto gratuito.`
        };

        return serviceDescriptions[serviceType] || t('reformsLanding.hero.description');
    };

    // Título SEO (SOLO para meta tags, usa lógica SEO)
    const getSEOTitle = () => {
        if (!locationName) return seoTranslations.reformsLanding.hero.title;

        const serviceTexts = {
            'integral': `Reforma integral en ${locationName}`,
            'bano': `Reforma de baño en ${locationName}`,
            'cocina': `Reforma de cocina en ${locationName}`,
            'general': `Reformas en ${locationName}`
        };

        return serviceTexts[serviceType] || seoTranslations.reformsLanding.hero.title;
    };

    // Descripción SEO (SOLO para meta tags, usa lógica SEO)
    const getSEODescription = () => {
        if (!locationName) return seoTranslations.reformsLanding.hero.description;

        const serviceDescriptions = {
            'integral': `Especialistes en reformes integrals a ${locationName}. Transformem la teva llar completament amb garantia de qualitat. Pressupost gratuït sense compromís.`,
            'bano': `Experts en reformes de bany a ${locationName}. Disseny modern, acabats de qualitat i terminis complerts. Sol·licita el teu pressupost gratis.`,
            'cocina': `Reformes de cuina a ${locationName} amb disseny personalitzat. Funcionalitat i estil per a la teva cuina ideal. Pressupost sense compromís.`,
            'general': `Empresa de reformes de confiança a ${locationName}. Més de 150 projectes completats amb garantia de 2 anys. Pressupost gratuït.`
        };

        return serviceDescriptions[serviceType] || seoTranslations.reformsLanding.hero.description;
    };

    // Mensaje de WhatsApp personalizado
    const getWhatsAppMessage = () => {
        const base = locationName
            ? `Hola, quiero información sobre reformas en ${locationName}`
            : 'Hola, quiero información sobre reformas';

        const serviceExtra = {
            'integral': ' - reforma integral',
            'bano': ' - reforma de baño',
            'cocina': ' - reforma de cocina',
            'general': ''
        };

        return encodeURIComponent(base + serviceExtra[serviceType]);
    };

    useEffect(() => {
        // Scroll to top on component mount
        window.scrollTo(0, 0);

        // Update meta description for SEO
        if (locationName) {
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.setAttribute('content', getCustomDescription());
            }
        }

        // Track page view en Google Ads
        trackPageView(
            routeLocation.pathname,
            `Reformas Landing - ${locationName || 'General'} - ${serviceType}`
        );
    }, [locationName, serviceType, routeLocation.pathname, trackPageView]);

    const handleCTAClick = (e, position, ctaType = 'whatsapp') => {
        // Track en Google Ads
        trackCTAClick(
            `reformas_${position}`,
            ctaType,
            {
                page: routeLocation.pathname,
                location: locationName || 'general',
                service: serviceType,
                language: t('meta.title') // Para saber el idioma
            }
        );

        // Track lead generation
        trackLead(ctaType, {
            source_page: 'reformas_landing',
            location: locationName || 'general',
            service_type: serviceType,
            cta_position: position
        });

        console.log(`[CTA Click] Position: ${position}, Type: ${ctaType}, Location: ${locationName}, Service: ${serviceType}`);
    };

    return (
        <>
            <SEOMetaTags
                title={getSEOTitle()}
                description={getSEODescription()}
                type="website"
            />
            {/* Hero Section - Optimized for Conversion */}
            <div className="mxd-section mxd-section-inner-headline padding-text-pre-block" style={{ paddingTop: '120px' }}>
                <div className="mxd-container grid-container">
                    <div className="mxd-block loading-wrap">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-12 mxd-grid-item no-margin text-center">
                                    <div className="mxd-block__content">
                                        <div className="mxd-block__inner-headline">
                                            <p className="mxd-point-subtitle mb-3">
                                                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                                                    width="20px" height="20px" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fill="currentColor" d="M19.6,9.6c0,0-3,0-4,0c-0.4,0-1.8-0.2-1.8-0.2c-0.6-0.1-1.1-0.2-1.6-0.6c-0.5-0.3-0.9-0.8-1.2-1.2
                        c-0.3-0.4-0.4-0.9-0.5-1.4c0,0-0.1-1.1-0.2-1.5c-0.1-1.1,0-4.4,0-4.4C10.4,0.2,10.2,0,10,0S9.6,0.2,9.6,0.4c0,0,0.1,3.3,0,4.4
                        c0,0.4-0.2,1.5-0.2,1.5C9.4,6.7,9.2,7.2,9,7.6C8.7,8.1,8.2,8.5,7.8,8.9c-0.5,0.3-1,0.5-1.6,0.6c0,0-1.2,0.1-1.7,0.2
                        c-1,0.1-4.2,0-4.2,0C0.2,9.6,0,9.8,0,10c0,0.2,0.2,0.4,0.4,0.4c0,0,3.1-0.1,4.2,0c0.4,0,1.7,0.2,1.7,0.2c0.6,0.1,1.1,0.2,1.6,0.6
                        c0.4,0.3,0.8,0.7,1.1,1.1c0.3,0.5,0.5,1,0.6,1.6c0,0,0.1,1.3,0.2,1.7c0,1,0,4.1,0,4.1c0,0.2,0.2,0.4,0.4,0.4s0.4-0.2,0.4-0.4
                        c0,0,0-3.1,0-4.1c0-0.4,0.2-1.7,0.2-1.7c0.1-0.6,0.2-1.1,0.6-1.6c0.3-0.4,0.7-0.8,1.1-1.1c0.5-0.3,1-0.5,1.6-0.6
                        c0,0,1.3-0.1,1.8-0.2c1,0,4,0,4,0c0.2,0,0.4-0.2,0.4-0.4C20,9.8,19.8,9.6,19.6,9.6L19.6,9.6z" />
                                                </svg>
                                                <span>{t('reformsLanding.hero.badge')}</span>
                                            </p>
                                            <h1 className="inner-headline__title animated-type loading__item" style={{ fontSize: '3.5rem', marginBottom: '2rem' }}>
                                                {getCustomTitle()}
                                            </h1>
                                            <p className="inner-headline__text t-large t-bright loading__item" style={{ fontSize: '1.3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
                                                {getCustomDescription()}
                                            </p>
                                            {locationName && (
                                                <p className="loading__item" style={{ fontSize: '1rem', marginBottom: '2rem', opacity: 0.8 }}>
                                                    📍 Servicio en {locationName} y alrededores
                                                </p>
                                            )}
                                            <div className="loading__item" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                <a
                                                    className="btn btn-anim btn-primary btn-large slide-right-up"
                                                    href={`https://wa.me/34610683948?text=${getWhatsAppMessage()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => handleCTAClick(e, 'hero')}
                                                >
                                                    <span className="btn-caption">{t('reformsLanding.hero.cta_primary')}</span>
                                                    <i className="ph-bold ph-whatsapp-logo"></i>
                                                </a>
                                                <a
                                                    className="btn btn-anim btn-secondary btn-large slide-right-up"
                                                    href="tel:+34610683948"
                                                    onClick={(e) => handleCTAClick(e, 'hero', 'phone')}
                                                >
                                                    <span className="btn-caption">{t('reformsLanding.hero.cta_secondary')}</span>
                                                    <i className="ph-bold ph-phone"></i>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Trust Badges Section */}
            <div className="mxd-section padding-default" style={{ paddingTop: '60px', paddingBottom: '40px' }}>
                <div className="mxd-container">
                    <div className="container-fluid px-0">
                        <div className="row gx-4 text-center">
                            <div className="col-12 col-md-3 mb-4 mb-md-0">
                                <div className="anim-uni-in-up">
                                    <h3 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>+150</h3>
                                    <p className="t-bright">{t('reformsLanding.badges.projects')}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-3 mb-4 mb-md-0">
                                <div className="anim-uni-in-up">
                                    <h3 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>98%</h3>
                                    <p className="t-bright">{t('reformsLanding.badges.satisfaction')}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-3 mb-4 mb-md-0">
                                <div className="anim-uni-in-up">
                                    <h3 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>30 días</h3>
                                    <p className="t-bright">{t('reformsLanding.badges.average_time')}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-3">
                                <div className="anim-uni-in-up">
                                    <h3 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>10 años</h3>
                                    <p className="t-bright">{t('reformsLanding.badges.experience')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Benefits Section */}
            <div className="mxd-section padding-default">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title text-center" style={{ marginBottom: '4rem' }}>
                            <h2 className="reveal-type" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
                                {t('reformsLanding.benefits.title')}
                            </h2>
                            <p className="anim-uni-in-up t-large" style={{ maxWidth: '700px', margin: '0 auto' }}>
                                {t('reformsLanding.benefits.subtitle')}
                            </p>
                        </div>
                        <div className="mxd-approach-list">
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0">
                                            <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__image anim-uni-in-up">
                                                    <i className="ph-bold ph-check-circle" style={{ fontSize: '3rem', color: 'var(--mxd-color-accent)' }}></i>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('reformsLanding.benefits.benefit1.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p>{t('reformsLanding.benefits.benefit1.description')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0">
                                            <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__image anim-uni-in-up">
                                                    <i className="ph-bold ph-sparkle" style={{ fontSize: '3rem', color: 'var(--mxd-color-accent)' }}></i>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('reformsLanding.benefits.benefit2.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p>{t('reformsLanding.benefits.benefit2.description')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0">
                                            <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__image anim-uni-in-up">
                                                    <i className="ph-bold ph-shield-check" style={{ fontSize: '3rem', color: 'var(--mxd-color-accent)' }}></i>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('reformsLanding.benefits.benefit3.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p>{t('reformsLanding.benefits.benefit3.description')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0">
                                            <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__image anim-uni-in-up">
                                                    <i className="ph-bold ph-calendar-check" style={{ fontSize: '3rem', color: 'var(--mxd-color-accent)' }}></i>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('reformsLanding.benefits.benefit4.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p>{t('reformsLanding.benefits.benefit4.description')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Mid Section */}
            <div className="mxd-section padding-default" style={{ background: 'var(--mxd-color-background-secondary)' }}>
                <div className="mxd-container">
                    <div className="text-center">
                        <h2 className="reveal-type" style={{ fontSize: '2rem', marginBottom: '2rem' }}>
                            {t('reformsLanding.cta_mid.title')}
                        </h2>
                        <a
                            className="btn btn-anim btn-primary btn-large slide-right-up"
                            href={`https://wa.me/34610683948?text=${encodeURIComponent('Quiero un presupuesto gratuito para mi reforma' + (locationName ? ` en ${locationName}` : ''))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => handleCTAClick(e, 'mid')}
                        >
                            <span className="btn-caption">{t('reformsLanding.cta_mid.button')}</span>
                            <i className="ph-bold ph-arrow-right"></i>
                        </a>
                    </div>
                </div>
            </div>

            {/* Process Section */}
            <div className="mxd-section padding-default">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title text-center" style={{ marginBottom: '4rem' }}>
                            <h2 className="reveal-type" style={{ fontSize: '2.5rem' }}>
                                {t('reformsLanding.process.title')}
                            </h2>
                        </div>
                        <div className="mxd-pinned-universal">
                            <div className="container-fluid px-0">
                                <div className="row gx-0">
                                    <div className="col-12 mxd-pinned-universal__scroll">
                                        <div className="mxd-pinned-universal__scroll-inner mxd-grid-item no-margin">
                                            <div className="mxd-res-list">
                                                <div className="mxd-res-list__item">
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                    <div className="mxd-res-list__content">
                                                        <div className="mxd-res-list__data">
                                                            <div className="mxd-res-list__number">
                                                                <span className="anim-uni-in-up" style={{ fontSize: '3rem', fontWeight: 'bold', color: 'var(--mxd-color-accent)' }}>01</span>
                                                            </div>
                                                            <div className="mxd-res-list__title">
                                                                <h4 className="anim-uni-in-up">{t('reformsLanding.process.step1.title')}</h4>
                                                            </div>
                                                            <div className="mxd-res-list__descr">
                                                                <p className="anim-uni-in-up">{t('reformsLanding.process.step1.description')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                </div>
                                                <div className="mxd-res-list__item">
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                    <div className="mxd-res-list__content">
                                                        <div className="mxd-res-list__data">
                                                            <div className="mxd-res-list__number">
                                                                <span className="anim-uni-in-up" style={{ fontSize: '3rem', fontWeight: 'bold', color: 'var(--mxd-color-accent)' }}>02</span>
                                                            </div>
                                                            <div className="mxd-res-list__title">
                                                                <h4 className="anim-uni-in-up">{t('reformsLanding.process.step2.title')}</h4>
                                                            </div>
                                                            <div className="mxd-res-list__descr">
                                                                <p className="anim-uni-in-up">{t('reformsLanding.process.step2.description')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                </div>
                                                <div className="mxd-res-list__item">
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                    <div className="mxd-res-list__content">
                                                        <div className="mxd-res-list__data">
                                                            <div className="mxd-res-list__number">
                                                                <span className="anim-uni-in-up" style={{ fontSize: '3rem', fontWeight: 'bold', color: 'var(--mxd-color-accent)' }}>03</span>
                                                            </div>
                                                            <div className="mxd-res-list__title">
                                                                <h4 className="anim-uni-in-up">{t('reformsLanding.process.step3.title')}</h4>
                                                            </div>
                                                            <div className="mxd-res-list__descr">
                                                                <p className="anim-uni-in-up">{t('reformsLanding.process.step3.description')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                </div>
                                                <div className="mxd-res-list__item">
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                    <div className="mxd-res-list__content">
                                                        <div className="mxd-res-list__data">
                                                            <div className="mxd-res-list__number">
                                                                <span className="anim-uni-in-up" style={{ fontSize: '3rem', fontWeight: 'bold', color: 'var(--mxd-color-accent)' }}>04</span>
                                                            </div>
                                                            <div className="mxd-res-list__title">
                                                                <h4 className="anim-uni-in-up">{t('reformsLanding.process.step4.title')}</h4>
                                                            </div>
                                                            <div className="mxd-res-list__descr">
                                                                <p className="anim-uni-in-up">{t('reformsLanding.process.step4.description')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mxd-res-list__divider anim-uni-in-up"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Guarantee Section */}
            <div className="mxd-section padding-default" style={{ background: 'var(--mxd-color-background-secondary)' }}>
                <div className="mxd-container">
                    <div className="text-center">
                        <i className="ph-bold ph-seal-check" style={{ fontSize: '4rem', color: 'var(--mxd-color-accent)', marginBottom: '1rem' }}></i>
                        <h2 className="reveal-type" style={{ fontSize: '2.5rem', marginBottom: '1.5rem' }}>
                            {t('reformsLanding.guarantee.title')}
                        </h2>
                        <p className="anim-uni-in-up t-large" style={{ maxWidth: '700px', margin: '0 auto 2rem' }}>
                            {t('reformsLanding.guarantee.description')}
                        </p>
                        <div className="row" style={{ maxWidth: '900px', margin: '0 auto' }}>
                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <div className="anim-uni-in-up">
                                    <i className="ph-bold ph-certificate" style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}></i>
                                    <p className="t-bright">{t('reformsLanding.guarantee.item1')}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <div className="anim-uni-in-up">
                                    <i className="ph-bold ph-users-three" style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}></i>
                                    <p className="t-bright">{t('reformsLanding.guarantee.item2')}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-4">
                                <div className="anim-uni-in-up">
                                    <i className="ph-bold ph-handshake" style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}></i>
                                    <p className="t-bright">{t('reformsLanding.guarantee.item3')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className="mxd-section padding-default">
                <div className="mxd-container">
                    <div className="mxd-section-title text-center" style={{ marginBottom: '4rem' }}>
                        <h2 className="reveal-type" style={{ fontSize: '2.5rem' }}>
                            {t('reformsLanding.faq.title')}
                        </h2>
                    </div>
                    <div className="row" style={{ maxWidth: '1000px', margin: '0 auto' }}>
                        <div className="col-12 col-md-6">
                            <div className="anim-uni-in-up" style={{ marginBottom: '2rem' }}>
                                <h5 style={{ marginBottom: '1rem' }}>{t('reformsLanding.faq.q1.question')}</h5>
                                <p className="t-bright">{t('reformsLanding.faq.q1.answer')}</p>
                            </div>
                            <div className="anim-uni-in-up" style={{ marginBottom: '2rem' }}>
                                <h5 style={{ marginBottom: '1rem' }}>{t('reformsLanding.faq.q2.question')}</h5>
                                <p className="t-bright">{t('reformsLanding.faq.q2.answer')}</p>
                            </div>
                            <div className="anim-uni-in-up" style={{ marginBottom: '2rem' }}>
                                <h5 style={{ marginBottom: '1rem' }}>{t('reformsLanding.faq.q3.question')}</h5>
                                <p className="t-bright">{t('reformsLanding.faq.q3.answer')}</p>
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <div className="anim-uni-in-up" style={{ marginBottom: '2rem' }}>
                                <h5 style={{ marginBottom: '1rem' }}>{t('reformsLanding.faq.q4.question')}</h5>
                                <p className="t-bright">{t('reformsLanding.faq.q4.answer')}</p>
                            </div>
                            <div className="anim-uni-in-up" style={{ marginBottom: '2rem' }}>
                                <h5 style={{ marginBottom: '1rem' }}>{t('reformsLanding.faq.q5.question')}</h5>
                                <p className="t-bright">{t('reformsLanding.faq.q5.answer')}</p>
                            </div>
                            <div className="anim-uni-in-up" style={{ marginBottom: '2rem' }}>
                                <h5 style={{ marginBottom: '1rem' }}>{t('reformsLanding.faq.q6.question')}</h5>
                                <p className="t-bright">{t('reformsLanding.faq.q6.answer')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Final CTA Section */}
            <div className="mxd-section overflow-hidden">
                <div className="mxd-container">
                    <div className="mxd-block">
                        <div className="mxd-promo">
                            <div className="mxd-promo__inner anim-zoom-out-container">
                                <div className="mxd-promo__bg"></div>
                                <div className="mxd-promo__content">
                                    <p className="mxd-promo__title anim-uni-in-up">
                                        <span className="mxd-promo__icon">
                                            <i className="ph-bold ph-rocket-launch"></i>
                                        </span>
                                        <span className="mxd-promo__caption reveal-type">{t('reformsLanding.cta_final.title')}</span>
                                    </p>
                                    <p className="mxd-promo__description" style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>
                                        {t('reformsLanding.cta_final.description')}
                                    </p>
                                    <div className="mxd-promo__controls anim-uni-in-up" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <a
                                            className="btn btn-anim btn-primary btn-large slide-right-up"
                                            href={`https://wa.me/34610683948?text=${encodeURIComponent('Quiero empezar mi reforma ahora' + (locationName ? ` en ${locationName}` : ''))}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => handleCTAClick(e, 'final')}
                                        >
                                            <span className="btn-caption">{t('reformsLanding.cta_final.button_primary')}</span>
                                            <i className="ph-bold ph-whatsapp-logo"></i>
                                        </a>
                                        <a
                                            className="btn btn-anim btn-secondary btn-large slide-right-up"
                                            href="tel:+34610683948"
                                            onClick={(e) => handleCTAClick(e, 'final', 'phone')}
                                        >
                                            <span className="btn-caption">{t('reformsLanding.cta_final.button_secondary')}</span>
                                            <i className="ph-bold ph-phone"></i>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReformationsLanding;

